import type { SpaceChallengeResponse, SpacePowSolution } from '../contracts/space';

const encoder = new TextEncoder();

function hex(bytes: ArrayBuffer): string {
  return [...new Uint8Array(bytes)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

function randomNonce(): string {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/g, '');
}

export async function sha256Hex(input: string): Promise<string> {
  return hex(await crypto.subtle.digest('SHA-256', encoder.encode(input)));
}

export function hasLeadingZeroHexNibbles(digest: string, difficulty: number): boolean {
  return String(digest || '').startsWith('0'.repeat(Math.max(0, difficulty)));
}

export async function solveSpacePow(
  challenge: SpaceChallengeResponse,
  options: { maxIterations?: number; abortSignal?: AbortSignal } = {},
): Promise<SpacePowSolution> {
  const maxIterations = options.maxIterations ?? 750_000;
  const startedAt = Date.now();
  for (let i = 0; i < maxIterations; i += 1) {
    if (options.abortSignal?.aborted) throw new DOMException('PoW aborted', 'AbortError');
    const nonce = `${randomNonce()}.${i.toString(36)}`;
    const digest = await sha256Hex(`${challenge.powPrefix}:${nonce}`);
    if (hasLeadingZeroHexNibbles(digest, challenge.difficulty)) {
      return {
        powNonce: nonce,
        digest,
        iterations: i + 1,
        solvedAt: Date.now(),
        elapsedMs: Date.now() - startedAt,
      };
    }
  }
  throw new Error('SPACE_POW_NOT_SOLVED');
}
